import * as Yup from 'yup'
import { useParams } from 'react-router-dom'
import { _ITERABLE_CONFIG } from '../../utils/empties'
import {
  useAllQuery,
  useSingleQuery,
} from '../../utils/hooks/reactQuery/queries'
import { useItemMutation } from '../../utils/hooks/reactQuery/mutations'
import { toast } from 'react-toastify'
import { Container, Row } from 'reactstrap'
import { Form, Formik } from 'formik'
import {
  BootstrapTextInput,
  FormCard,
  SaveButton,
  TypeaheadMultiInput,
  TypeaheadSingleInput,
  TypeaheadTagsInput,
} from '../../components/forms'
import React from 'react'

const SEND_MODES = ['RecipientTimeZone', 'ProjectTimeZone']
const TYPES = ['Email', 'SMS']

const IterableConfigSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  tags: Yup.array().of(Yup.string()),
  type: Yup.string().oneOf(TYPES).required('Type is required'),
  templateId: Yup.number().required('Template is required'),
  templateFile: Yup.string().nullable(),
  listIds: Yup.array()
    .of(Yup.number())
    .min(1, 'At least one list is required')
    .required('Lists is required'),
  suppressionListIds: Yup.array().of(Yup.number()),
  sendMode: Yup.string().oneOf(SEND_MODES),
  campaignPrefix: Yup.string(),
  startTimeZone: Yup.string(),
})

function IterableConfig() {
  const { id } = useParams()
  const {
    data = _ITERABLE_CONFIG,
    isLoading,
    isError,
    error,
  } = useSingleQuery('iterable-config', id)
  const { update, add } = useItemMutation('iterable-config')
  const { data: templates = [] } = useAllQuery('iterable-templates')
  const { data: smsTemplates = [] } = useAllQuery('iterable-sms-templates')
  const { data: lists = [] } = useAllQuery('iterable-lists')
  const { data: templateFiles = [] } = useAllQuery('template-files')

  const handleSubmit = (values, { setSubmitting }) => {
    const toastId = toast.loading('Saving iterable config...')

    // Are we updating or adding?
    if (id !== 'new') {
      update.mutate(values, {
        onSuccess: () => {
          toast.update(toastId, {
            render: 'Saved iterable config!',
            type: 'success',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
        onError: (error) => {
          console.error(error)
          toast.update(toastId, {
            render: 'Could not save iterable config...',
            type: 'error',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
      })
    } else {
      add.mutate(values, {
        onSuccess: () => {
          toast.update(toastId, {
            render: 'Saved iterable config!',
            type: 'success',
            isLoading: false,
            autoClose: 1500,
          })
        },
        onError: (error) => {
          console.error(error)
          toast.update(toastId, {
            render: 'Could not save iterable config...',
            type: 'error',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
      })
    }
  }

  return (
    <Container fluid>
      {isLoading ? (
        <h5>Loading...</h5>
      ) : isError ? (
        <h5>Error: {error.message}</h5>
      ) : (
        <FormCard
          breadcrumbLink="/dashboard/iterable-configs"
          breadcrumbLabel="Iterable Configs"
          breadcrumbItem={
            data.name.length > 0 ? data.name : 'New Iterable Config'
          }
        >
          <Formik
            initialValues={data}
            validationSchema={IterableConfigSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ isValid, isSubmitting, values, errors }) => (
              <Form>
                <Row>
                  <BootstrapTextInput
                    label="Name"
                    id="name"
                    name="name"
                    placeholder="Name"
                  />
                  <TypeaheadTagsInput />
                  <TypeaheadSingleInput
                    label="Type"
                    id="type"
                    name="type"
                    placeholder="Type"
                    options={TYPES}
                  />
                  {values.type === 'SMS' && (
                    <>
                      <BootstrapTextInput
                        label="Campaign Prefix"
                        id="campaignPrefix"
                        name="campaignPrefix"
                        placeholder="Campaign Prefix"
                      />
                      <TypeaheadSingleInput
                        label="Template File"
                        id="templateFile"
                        name="templateFile"
                        placeholder="Template File"
                        objects
                        labelKey="name"
                        valueKey="_id"
                        options={templateFiles.filter(
                          (templateFile) => templateFile.type === 'SMS',
                        )}
                      />
                    </>
                  )}
                  <TypeaheadSingleInput
                    label="Iterable Template"
                    id="templateId"
                    name="templateId"
                    placeholder="Iterable Template"
                    objects
                    labelKey="name"
                    valueKey="templateId"
                    options={values.type === 'SMS' ? smsTemplates : templates}
                  />
                  <TypeaheadMultiInput
                    label="Lists"
                    id="listIds"
                    name="listIds"
                    placeholder="Lists"
                    options={lists}
                    objects
                    labelKey="name"
                    valueKey="id"
                  />
                  <TypeaheadMultiInput
                    label="Suppression Lists"
                    id="suppressionListIds"
                    name="suppressionListIds"
                    placeholder="Suppression Lists"
                    options={lists}
                    objects
                    labelKey="name"
                    valueKey="id"
                  />
                  <TypeaheadSingleInput
                    label="Send Mode"
                    id="sendMode"
                    name="sendMode"
                    placeholder="Send Mode"
                    options={SEND_MODES}
                  />
                  <TypeaheadSingleInput
                    label="Start Time Zone"
                    id="startTimeZone"
                    name="startTimeZone"
                    placeholder="Start Time Zone"
                    options={[
                      'America/New_York',
                      'America/Chicago',
                      'America/Denver',
                      'America/Los_Angeles',
                    ]}
                  />
                </Row>
                <SaveButton disabled={isSubmitting || !isValid} />
              </Form>
            )}
          </Formik>
        </FormCard>
      )}
    </Container>
  )
}

export default IterableConfig
